import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import { useQwanta, QwantaContext, persist } from "qwanyx";
import {
    FormControl,
    FormLabel,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Button,
    IconButton,
    Select,
    MenuItem
} from "@mui/material";
import {
    Add as AddIcon,
    Clear as ClearIcon,
    Edit as EditIcon
} from "@mui/icons-material";

// Custom hook to detect print mode
const usePrintMode = () => {
    const [isPrinting, setIsPrinting] = useState(false);

    useEffect(() => {
        const handleBeforePrint = () => setIsPrinting(true);
        const handleAfterPrint = () => setIsPrinting(false);

        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);

        const mediaQuery = window.matchMedia('print');
        const handleMediaChange = (e) => setIsPrinting(e.matches);
        mediaQuery.addEventListener('change', handleMediaChange);

        setIsPrinting(mediaQuery.matches);

        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
            mediaQuery.removeEventListener('change', handleMediaChange);
        };
    }, []);

    return isPrinting;
};

export const Table = ({
    name = "Table",
    qwantumId,
    onSave,
    headers = ["Column 1", "Column 2", "Column 3"],
    rows = [
        ["Row 1 Cell 1", "Row 1 Cell 2", "Row 1 Cell 3"],
        ["Row 2 Cell 1", "Row 2 Cell 2", "Row 2 Cell 3"]
    ],
    columnWidths = ["33.33%", "33.33%", "33.34%"],
    columnAlignments = ["left", "left", "left"],
    invoiceMode = false,
    currency = "$",
    vatRate = 20,
    language = "english",
    striped = false,
    bordered = false,
    hoverable = false,
    fullwidth = false,
    narrow = false,
    size = "normal",
    editable = true,
    allowEditing = true,
    showFooter = false,
    width = "100%",
    height = "auto",
    style = {},
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
        actions: { setProp }
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // Qwanta persistence
    const [persistedRows, setPersistedRows] = useQwanta(qwantumId);
    const [isEditing, setIsEditing] = useState(false);
    //const [value, set] = useQwanta(qwantumId);
    const qwantaContext = useContext(QwantaContext);

    const [hasLoadedPersisted, setHasLoadedPersisted] = useState(false);

    // Local state for current editing - initialize with rows
    const [localRows, setLocalRows] = useState(rows);

    const showEditAlert = (rowIndex, colIndex, newValue, oldValue) => {
        alert(`Cell [${rowIndex + 1}, ${colIndex + 1}] updated from "${oldValue}" to "${newValue}"`);
    };
    const hasLoadedRef = useRef(false);
    // Load persisted data when it becomes available (once only)
    useEffect(() => {
        if (qwantumId && persistedRows && Array.isArray(persistedRows) && !hasLoadedRef.current) {
            setLocalRows(persistedRows);
            setProp(props => props.rows = persistedRows);
            //setHasLoadedPersisted(true);
            hasLoadedRef.current = true;
            const test = 'toto'
        }
    }, [persistedRows]);

    // Use localRows as the source of truth for display
    const effectiveRows = localRows;
    const [prevValue, setPrevValue] = useState(JSON.stringify(effectiveRows));

    // Update prevValue when effectiveRows changes
    useEffect(() => {
        setPrevValue(JSON.stringify(effectiveRows));
    }, [effectiveRows]);

    const isPrinting = usePrintMode();
    const showActionColumn = allowEditing && !isPrinting;

    const [editingCell, setEditingCell] = useState(null);
    const [editingValue, setEditingValue] = useState('');

    // Translation system
    const translations = {
        english: {
            total: "Total",
            tax: "VAT",
            totalIncludingTax: "Total Including Tax",
            designation: "Designation",
            quantity: "Quantity",
            price: "Price",
            amount: "Amount"
        },
        french: {
            total: "Total",
            tax: "TVA",
            totalIncludingTax: "Total TTC",
            designation: "Désignation",
            quantity: "Quantité",
            price: "Prix",
            amount: "Montant"
        }
    };

    const t = translations[language] || translations.english;

    // Invoice mode configuration
    useEffect(() => {
        if (invoiceMode) {
            const invoiceHeaders = [t.designation, t.quantity, t.price, t.amount];
            const invoiceWidths = ["70%", "10%", "10%", "10%"];
            const invoiceAlignments = ["left", "center", "right", "right"];
            const invoiceRows = [
                ["Product/Service", "1", "100.00", "100.00"],
                ["Another item", "2", "50.00", "100.00"]
            ];

            setProp(props => {
                props.headers = invoiceHeaders;
                props.columnWidths = invoiceWidths;
                props.columnAlignments = invoiceAlignments;
                props.rows = invoiceRows;
                props.showFooter = true;
            });
        }
    }, [invoiceMode, language, setProp]);

    // Invoice calculations
    const formatCurrency = (amount) => {
        const num = parseFloat(amount) || 0;
        return `${currency}${num.toFixed(2)}`;
    };

    const calculateAmount = (quantity, price) => {
        const qty = parseFloat(quantity) || 0;
        const prc = parseFloat(price) || 0;
        return (qty * prc).toFixed(2);
    };

    const calculateInvoiceTotals = () => {
        if (!invoiceMode) return { total: 0, vat: 0, ttc: 0 };

        const total = effectiveRows.reduce((sum, row) => {
            const amount = parseFloat(row[3]) || 0;
            return sum + amount;
        }, 0);

        const vat = (total * vatRate) / 100;
        const ttc = total + vat;

        return { total, vat, ttc };
    };

    const { total, vat, ttc } = calculateInvoiceTotals();

    const getSelectionStyle = () => {
        if (!enabled) return {};
        if (isSelected) {
            return {
                outline: '1px dashed #2196f3',
                outlineOffset: '2px'
            };
        }
        if (isHovered) {
            return {
                outline: '1px dashed #ff9800',
                outlineOffset: '1px'
            };
        }
        return {};
    };

    const getTableClasses = () => {
        let classes = ["table"];
        if (striped) classes.push("is-striped");
        if (hoverable) classes.push("is-hoverable");
        if (fullwidth) classes.push("is-fullwidth");
        if (narrow) classes.push("is-narrow");
        if (size !== "normal") classes.push(`is-${size}`);
        return classes.join(" ");
    };

    const addRow = () => {
        const newRow = invoiceMode
            ? ["New item", "1", "0.00", "0.00"]
            : headers.map(() => "New Cell");
        const updatedRows = [...effectiveRows, newRow];

        // Just visual update
        setLocalRows(updatedRows);
    };

    const removeRow = (index) => {
        if (effectiveRows.length > 1) {
            const updatedRows = effectiveRows.filter((_, i) => i !== index);

            // Just visual update
            setLocalRows(updatedRows);
        }
    };

    const addColumn = () => {
        const newHeaders = [...headers, `Column ${headers.length + 1}`];
        const updatedRows = effectiveRows.map(row => [...row, "New Cell"]);
        const newWidth = "10%";
        const adjustedWidths = columnWidths.map(width => {
            const numValue = parseFloat(width);
            const unit = width.replace(numValue.toString(), '');
            return `${(numValue * 0.9).toFixed(2)}${unit}`;
        });
        const newColumnWidths = [...adjustedWidths, newWidth];
        const newColumnAlignments = [...columnAlignments, "left"];

        // Visual update
        setLocalRows(updatedRows);

        // Update Craft.js props for headers/widths/alignments only
        if (enabled || allowEditing) {
            setProp(props => {
                props.headers = newHeaders;
                props.columnWidths = newColumnWidths;
                props.columnAlignments = newColumnAlignments;
            });
        }
    };

    const removeColumn = (index) => {
        if (headers.length > 1) {
            const newHeaders = headers.filter((_, i) => i !== index);
            const updatedRows = effectiveRows.map(row => row.filter((_, i) => i !== index));
            const newColumnWidths = columnWidths.filter((_, i) => i !== index);
            const newColumnAlignments = columnAlignments.filter((_, i) => i !== index);

            // Visual update
            setLocalRows(updatedRows);

            // Update Craft.js props for headers/widths/alignments only
            if (enabled || allowEditing) {
                setProp(props => {
                    props.headers = newHeaders;
                    props.columnWidths = newColumnWidths;
                    props.columnAlignments = newColumnAlignments;
                });
            }
        }
    };

    const updateHeader = (index, value) => {
        const newHeaders = [...headers];
        newHeaders[index] = value;

        if (enabled || allowEditing) {
            setProp(props => props.headers = newHeaders);
        }
    };

    const updateCell = (rowIndex, colIndex, value) => {
        const updatedRows = effectiveRows.map((row, rIdx) => {
            if (rIdx === rowIndex) {
                const newRow = [...row];
                newRow[colIndex] = value;

                // Auto-calculate Amount in invoice mode
                if (invoiceMode && (colIndex === 1 || colIndex === 2)) {
                    const quantity = colIndex === 1 ? value : newRow[1];
                    const price = colIndex === 2 ? value : newRow[2];
                    newRow[3] = calculateAmount(quantity, price);
                }

                return newRow;
            }
            return [...row];
        });

        // Just update visual state
        setLocalRows(updatedRows);
        //setPersistedRows(updatedRows);
    };

    const handleCellClick = (rowIndex, colIndex) => {
        if (allowEditing) {
            if (invoiceMode && colIndex === 3) return;
            setIsEditing(true);
            setEditingCell({ row: rowIndex, col: colIndex });
            setEditingValue(effectiveRows[rowIndex][colIndex]);
        }
    };

    const handleCellChange = (e) => {
        const newValue = e.target.value;
        setEditingValue(newValue);

        // Just visual update
        if (editingCell) {
            updateCell(editingCell.row, editingCell.col, newValue);
        }

    };

    const handleCellBlur = () => {
        if (editingCell) {
            const oldValue = effectiveRows[editingCell.row][editingCell.col];
            const newValue = editingValue;

            if (oldValue !== newValue) {
                showEditAlert(editingCell.row, editingCell.col, newValue, oldValue);
            }
        }

        setEditingCell(null);
        setEditingValue('');

        // Persist after state cleanup
        const delta = { [qwantumId]: effectiveRows };
        persist(delta, qwantaContext._id, qwantaContext.di, qwantaContext.ed, false)
    };

    const handleCellKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCellBlur();
        }
    };
    

    // Persist only on table blur (backup mechanism)
    const handleTableBlur = (e) => {
        // Don't persist if we're currently editing a cell
        if (editingCell) return;
        //setPersistedRows(effectiveRows);
        if (!e.currentTarget.contains(e.relatedTarget)) {
            const newValue = JSON.stringify(effectiveRows);

            if (newValue !== prevValue) {
                if (qwantumId && setPersistedRows) {
                    setPersistedRows(effectiveRows);
                }
                if (onSave) {
                    onSave(newValue);
                }
                setPrevValue(newValue);
            }
        }
    };

    const containerStyle = {
        width,
        height,
        overflow: 'auto',
        position: 'relative',
        ...style,
        ...getSelectionStyle()
    };

    return (
        <div
            ref={ref => {
                if (enabled) {
                    connect(drag(ref));
                } else {
                    connect(ref);
                }
            }}
            name={name}
            style={containerStyle}
            className="table-container"
            onBlur={handleTableBlur}
            tabIndex={0}
            {...props}
        >
            <style>
                {`
                @media print {
                    .table-action-column {
                        display: none !important;
                    }
                    
                    .table-container {
                        zoom: 0.6;
                        overflow: hidden !important;
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                    
                    .table {
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                }
                `}
            </style>
            <table className={getTableClasses()} style={{ tableLayout: 'fixed' }}>
                <colgroup>
                    {columnWidths.map((width, index) => (
                        <col key={index} style={{
                            width,
                            textAlign: columnAlignments[index] || 'left'
                        }} />
                    ))}
                    {showActionColumn && <col style={{ width: '32px' }} className="table-action-column" />}
                </colgroup>
                <thead>
                    <tr style={{
                        borderBottom: '2px solid #ddd'
                    }}>
                        {headers.map((header, index) => (
                            <th key={index} style={{
                                textAlign: columnAlignments[index] || 'left',
                                border: 'none'
                            }}>
                                {enabled && editable && !invoiceMode ? (
                                    <input
                                        type="text"
                                        value={header}
                                        onChange={(e) => updateHeader(index, e.target.value)}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            width: '100%',
                                            fontWeight: 'bold',
                                            textAlign: columnAlignments[index] || 'left'
                                        }}
                                    />
                                ) : (
                                    header
                                )}
                                {enabled && editable && !invoiceMode && (
                                    <IconButton
                                        onClick={() => removeColumn(index)}
                                        disabled={headers.length <= 1}
                                        size="small"
                                        style={{
                                            marginLeft: '5px',
                                            color: 'black',
                                            width: '20px',
                                            height: '20px'
                                        }}
                                    >
                                        <ClearIcon style={{ fontSize: '14px' }} />
                                    </IconButton>
                                )}
                            </th>
                        ))}
                        {((enabled && editable) || showActionColumn) && (
                            <th style={{
                                border: 'none',
                                textAlign: 'center',
                                verticalAlign: 'middle'
                            }} className="table-action-column">
                                <div style={{
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {showActionColumn && (
                                        <IconButton
                                            onClick={addRow}
                                            size="small"
                                            style={{
                                                color: 'black',
                                                width: '24px',
                                                height: '24px'
                                            }}
                                        >
                                            <AddIcon style={{ fontSize: '16px' }} />
                                        </IconButton>
                                    )}
                                    {enabled && editable && !invoiceMode && (
                                        <button
                                            onClick={addColumn}
                                            style={{
                                                background: '#00d1b2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '3px',
                                                padding: '2px 6px',
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            +col
                                        </button>
                                    )}
                                </div>
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {effectiveRows.map((row, rowIndex) => (
                        <tr key={rowIndex} style={{
                            borderBottom: bordered ? '1px solid #dbdbdb' : 'none'
                        }}>
                            {row.map((cell, colIndex) => (
                                <td
                                    key={colIndex}
                                    onClick={() => handleCellClick(rowIndex, colIndex)}
                                    style={{
                                        cursor: allowEditing ? 'pointer' : 'default',
                                        textAlign: columnAlignments[colIndex] || 'left',
                                        borderLeft: bordered && colIndex > 0 ? '1px solid #dbdbdb' : 'none',
                                        borderRight: bordered ? '1px solid #dbdbdb' : 'none'
                                    }}
                                >
                                    {editingCell && editingCell.row === rowIndex && editingCell.col === colIndex ? (
                                        <input
                                            type="text"
                                            value={editingValue}
                                            onChange={handleCellChange}
                                            onBlur={handleCellBlur}
                                            onKeyDown={handleCellKeyDown}
                                            autoFocus
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                width: '100%',
                                                textAlign: columnAlignments[colIndex] || 'left'
                                            }}
                                        />
                                    ) : (
                                        <span style={{
                                            color: invoiceMode && colIndex === 3 ? '#666' : 'inherit'
                                        }}>
                                            {invoiceMode && (colIndex === 2 || colIndex === 3) ?
                                                formatCurrency(cell) : cell}
                                        </span>
                                    )}
                                </td>
                            ))}
                            {allowEditing && (
                                <td style={{
                                    borderLeft: bordered ? '1px solid #dbdbdb' : 'none',
                                    borderRight: bordered ? '1px solid #dbdbdb' : 'none',
                                    textAlign: 'center',
                                    verticalAlign: 'middle'
                                }}>
                                    <IconButton
                                        onClick={() => removeRow(rowIndex)}
                                        disabled={effectiveRows.length <= 1}
                                        size="small"
                                        style={{
                                            color: 'black',
                                            width: '24px',
                                            height: '24px'
                                        }}
                                    >
                                        <ClearIcon style={{ fontSize: '16px' }} />
                                    </IconButton>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
                {showFooter && (
                    <tfoot>
                        {invoiceMode ? (
                            <>
                                <tr>
                                    <td colSpan={((enabled && editable) || showActionColumn) ? "5" : "4"} style={{
                                        borderTop: '2px solid #ddd',
                                        height: '0px',
                                        padding: '0px'
                                    }}></td>
                                </tr>
                                <tr style={{ border: 'none' }}>
                                    <th colSpan={(enabled && editable) || allowEditing ? "3" : "3"} style={{
                                        textAlign: 'right',
                                        border: 'none',
                                        padding: '8px'
                                    }}>{t.total}:</th>
                                    <th style={{
                                        textAlign: 'right',
                                        border: 'none',
                                        padding: '8px'
                                    }}>{formatCurrency(total)}</th>
                                    {((enabled && editable) || allowEditing) && <th style={{ border: 'none' }}></th>}
                                </tr>
                                <tr style={{ border: 'none' }}>
                                    <th colSpan={(enabled && editable) || allowEditing ? "3" : "3"} style={{
                                        textAlign: 'right',
                                        border: 'none',
                                        padding: '8px'
                                    }}>{t.tax} ({vatRate}%):</th>
                                    <th style={{
                                        textAlign: 'right',
                                        border: 'none',
                                        padding: '8px'
                                    }}>{formatCurrency(vat)}</th>
                                    {((enabled && editable) || allowEditing) && <th style={{ border: 'none' }}></th>}
                                </tr>
                                <tr style={{ border: 'none' }}>
                                    <th colSpan={(enabled && editable) || allowEditing ? "3" : "3"} style={{
                                        textAlign: 'right',
                                        fontWeight: 'bold',
                                        border: 'none',
                                        padding: '8px',
                                        borderTop: '1px solid #000'
                                    }}>{t.totalIncludingTax}:</th>
                                    <th style={{
                                        textAlign: 'right',
                                        fontWeight: 'bold',
                                        border: 'none',
                                        padding: '8px',
                                        borderTop: '1px solid #000'
                                    }}>{formatCurrency(ttc)}</th>
                                    {((enabled && editable) || allowEditing) && <th style={{ border: 'none', borderTop: '1px solid #000' }}></th>}
                                </tr>
                            </>
                        ) : (
                            <tr>
                                {headers.map((header, index) => (
                                    <th key={index} style={{
                                        textAlign: columnAlignments[index] || 'left'
                                    }}>
                                        {header}
                                    </th>
                                ))}
                                {((enabled && editable) || allowEditing) && <th></th>}
                            </tr>
                        )}
                    </tfoot>
                )}
            </table>
        </div>
    );
};

const TableSettings = () => {
    const {
        actions: { setProp },
        name,
        qwantumId,
        headers,
        rows,
        columnWidths,
        columnAlignments,
        invoiceMode,
        currency,
        vatRate,
        language,
        striped,
        bordered,
        hoverable,
        fullwidth,
        narrow,
        size,
        editable,
        allowEditing,
        showFooter,
        width,
        height
    } = useNode((node) => ({
        name: node.data.props.name,
        qwantumId: node.data.props.qwantumId,
        headers: node.data.props.headers,
        rows: node.data.props.rows,
        columnWidths: node.data.props.columnWidths,
        columnAlignments: node.data.props.columnAlignments,
        invoiceMode: node.data.props.invoiceMode,
        currency: node.data.props.currency,
        vatRate: node.data.props.vatRate,
        language: node.data.props.language,
        striped: node.data.props.striped,
        bordered: node.data.props.bordered,
        hoverable: node.data.props.hoverable,
        fullwidth: node.data.props.fullwidth,
        narrow: node.data.props.narrow,
        size: node.data.props.size,
        editable: node.data.props.editable,
        allowEditing: node.data.props.allowEditing,
        showFooter: node.data.props.showFooter,
        width: node.data.props.width,
        height: node.data.props.height
    }));

    const [localName, setLocalName] = useState(name);
    const [localWidth, setLocalWidth] = useState(width);
    const [localHeight, setLocalHeight] = useState(height);

    useEffect(() => setLocalName(name), [name]);
    useEffect(() => setLocalWidth(width), [width]);
    useEffect(() => setLocalHeight(height), [height]);

    return (
        <div>
            <FormLabel>Component Name</FormLabel>
            <TextField
                fullWidth
                margin="normal"
                value={localName || ''}
                onChange={(e) => setLocalName(e.target.value)}
                onBlur={(e) => setProp(props => props.name = e.target.value)}
                size="small"
            />

            <FormLabel>Quantum ID</FormLabel>
            <TextField
                fullWidth
                margin="normal"
                value={qwantumId || ''}
                onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
                size="small"
                placeholder="Enter quantum ID for row data persistence"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={invoiceMode}
                        onChange={(e) => setProp(props => props.invoiceMode = e.target.checked)}
                    />
                }
                label="Invoice Mode"
                style={{ marginTop: '16px', marginBottom: '16px' }}
            />

            {invoiceMode && (
                <Box style={{ marginBottom: '16px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <FormLabel>Invoice Settings</FormLabel>

                    <FormLabel style={{ marginTop: '8px' }}>Language</FormLabel>
                    <Select
                        fullWidth
                        value={language}
                        onChange={(e) => setProp(props => props.language = e.target.value)}
                        size="small"
                    >
                        <MenuItem value="english">English</MenuItem>
                        <MenuItem value="french">French</MenuItem>
                    </Select>

                    <FormLabel style={{ marginTop: '8px' }}>Currency</FormLabel>
                    <Select
                        fullWidth
                        value={currency}
                        onChange={(e) => setProp(props => props.currency = e.target.value)}
                        size="small"
                    >
                        <MenuItem value="$">Dollar ($)</MenuItem>
                        <MenuItem value="€">Euro (€)</MenuItem>
                    </Select>

                    <FormLabel style={{ marginTop: '8px' }}>Tax Rate (%)</FormLabel>
                    <TextField
                        fullWidth
                        type="number"
                        value={vatRate}
                        onChange={(e) => setProp(props => props.vatRate = parseFloat(e.target.value) || 0)}
                        size="small"
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                </Box>
            )}

            <FormLabel>Width</FormLabel>
            <TextField
                fullWidth
                margin="normal"
                value={localWidth}
                onChange={(e) => setLocalWidth(e.target.value)}
                onBlur={(e) => setProp(props => props.width = e.target.value)}
                size="small"
                placeholder="100%, 500px, auto"
            />

            <FormLabel>Height</FormLabel>
            <TextField
                fullWidth
                margin="normal"
                value={localHeight}
                onChange={(e) => setLocalHeight(e.target.value)}
                onBlur={(e) => setProp(props => props.height = e.target.value)}
                size="small"
                placeholder="auto, 400px, 100vh"
            />

            <FormLabel>Table Size</FormLabel>
            <Select
                fullWidth
                margin="normal"
                value={size}
                onChange={(e) => setProp(props => props.size = e.target.value)}
                size="small"
            >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
            </Select>

            <FormLabel>Column Widths</FormLabel>
            <Box style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px', marginTop: '8px' }}>
                {invoiceMode ? (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Column widths are automatically set in invoice mode
                    </div>
                ) : (
                    headers.map((header, index) => (
                        <Box key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <Box style={{ minWidth: '100px', fontSize: '14px' }}>
                                {header}:
                            </Box>
                            <TextField
                                size="small"
                                value={columnWidths[index] || ''}
                                onChange={(e) => {
                                    const newWidths = [...columnWidths];
                                    newWidths[index] = e.target.value;
                                    setProp(props => props.columnWidths = newWidths);
                                }}
                                placeholder="25%, 200px, 1fr"
                                style={{ flex: 1 }}
                            />
                        </Box>
                    ))
                )}
            </Box>

            <FormLabel>Column Alignments</FormLabel>
            <Box style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px', marginTop: '8px' }}>
                {invoiceMode ? (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Column alignments are automatically set in invoice mode
                    </div>
                ) : (
                    headers.map((header, index) => (
                        <Box key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px'
                        }}>
                            <Box style={{ minWidth: '100px', fontSize: '14px' }}>
                                {header}:
                            </Box>
                            <Select
                                size="small"
                                value={columnAlignments[index] || 'left'}
                                onChange={(e) => {
                                    const newAlignments = [...columnAlignments];
                                    newAlignments[index] = e.target.value;
                                    setProp(props => props.columnAlignments = newAlignments);
                                }}
                                style={{ flex: 1 }}
                            >
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                            </Select>
                        </Box>
                    ))
                )}
            </Box>

            <Box style={{ marginTop: '16px', marginBottom: '16px' }}>
                <FormLabel>Table Styling</FormLabel>

                <FormControlLabel
                    control={
                        <Switch
                            checked={striped}
                            onChange={(e) => setProp(props => props.striped = e.target.checked)}
                        />
                    }
                    label="Striped rows"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={bordered}
                            onChange={(e) => setProp(props => props.bordered = e.target.checked)}
                        />
                    }
                    label="Bordered"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={hoverable}
                            onChange={(e) => setProp(props => props.hoverable = e.target.checked)}
                        />
                    }
                    label="Hoverable rows"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={fullwidth}
                            onChange={(e) => setProp(props => props.fullwidth = e.target.checked)}
                        />
                    }
                    label="Full width"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={narrow}
                            onChange={(e) => setProp(props => props.narrow = e.target.checked)}
                        />
                    }
                    label="Narrow"
                />
            </Box>

            <FormControlLabel
                control={
                    <Switch
                        checked={editable}
                        onChange={(e) => setProp(props => props.editable = e.target.checked)}
                    />
                }
                label="Editable (in edit mode)"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={allowEditing}
                        onChange={(e) => setProp(props => props.allowEditing = e.target.checked)}
                    />
                }
                label="Allow Content Editing"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={showFooter}
                        onChange={(e) => setProp(props => props.showFooter = e.target.checked)}
                    />
                }
                label="Show footer"
            />

            <Box style={{ marginTop: '16px' }}>
                <FormLabel>Table Data</FormLabel>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Headers: {headers.length} columns<br />
                    Rows: {rows.length} rows<br />
                    Only cell data is persisted, not configuration
                </div>
            </Box>
        </div>
    );
};

Table.craft = {
    props: {
        name: "Table",
        qwantumId: "",
        headers: ["Column 1", "Column 2", "Column 3"],
        rows: [
            ["Row 1 Cell 1", "Row 1 Cell 2", "Row 1 Cell 3"],
            ["Row 2 Cell 1", "Row 2 Cell 2", "Row 2 Cell 3"]
        ],
        columnWidths: ["33.33%", "33.33%", "33.34%"],
        columnAlignments: ["left", "left", "left"],
        invoiceMode: false,
        currency: "$",
        vatRate: 20,
        language: "english",
        striped: false,
        bordered: false,
        hoverable: false,
        fullwidth: false,
        narrow: false,
        size: "normal",
        editable: true,
        allowEditing: true,
        showFooter: false,
        width: "100%",
        height: "auto"
    },
    related: {
        settings: TableSettings
    },
    displayName: "Table"
};